import { useState } from "react"
import { ErrorMessage, Field, Form, Formik } from "formik"
import { number, object } from "yup"
import {
  CaretDown,
  ClockCounterClockwise,
  Minus,
  Plus,
} from "@phosphor-icons/react"
import { formatISODate } from "../../../utils/datetime"
import { useUpdateGlobalSetting } from "../../../hooks/useGlobalSettings"
import SettingHistory from "./SettingHistory"

// The API sends min/max with each setting, so the form enforces exactly the
// bounds the controller enforces instead of duplicating them here.
const buildValidationSchema = (setting) => {
  let value = number()
    .label(setting.label)
    .typeError(`${setting.label} must be a whole number`)
    .integer()
    .required()

  if (setting.min !== undefined && setting.min !== null) {
    value = value.min(setting.min)
  }
  if (setting.max !== undefined && setting.max !== null) {
    value = value.max(setting.max)
  }

  return object().shape({ value })
}

const clamp = (value, setting) => {
  const min = setting.min ?? Number.MIN_SAFE_INTEGER
  const max = setting.max ?? Number.MAX_SAFE_INTEGER
  return Math.min(Math.max(value, min), max)
}

// UpdatedAt/UpdatedBy stay null until the setting is edited for the first time,
// so the line falls back to who created it. "System" covers the seeded row,
// which has no user behind it.
const describeLastChange = (setting) => {
  if (setting.isDefault) return "Never changed — showing the built-in default"

  if (setting.updatedAt) {
    return `Last changed by ${setting.updatedBy || "System"} on ${formatISODate(
      setting.updatedAt
    )}`
  }

  return `Created by ${setting.createdBy || "System"} on ${formatISODate(
    setting.createdAt
  )}`
}

const stepperButtonClasses =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-300 text-gray-600 transition hover:border-secondary hover:text-secondary disabled:cursor-not-allowed disabled:opacity-40"

const SettingCard = ({ setting }) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const updateMutation = useUpdateGlobalSetting()

  const handleSubmit = async (values) => {
    await updateMutation.mutateAsync({ key: setting.key, value: values.value })
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-primary">
              {setting.label}
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-500">
              {setting.description}
            </p>
          </div>
          {setting.isDefault && (
            <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              Default
            </span>
          )}
        </div>

        <Formik
          enableReinitialize
          initialValues={{ value: setting.value }}
          validationSchema={buildValidationSchema(setting)}
          onSubmit={handleSubmit}
        >
          {({ dirty, isSubmitting, resetForm, setFieldValue, values }) => (
            <Form className="mt-6">
              <label
                className="text-xs font-medium uppercase tracking-wide text-gray-500"
                htmlFor={`${setting.key}-value`}
              >
                Value
              </label>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  className={stepperButtonClasses}
                  disabled={isSubmitting || values.value <= setting.min}
                  type="button"
                  onClick={() =>
                    setFieldValue(
                      "value",
                      clamp(Number(values.value) - 1, setting)
                    )
                  }
                >
                  <Minus size={14} weight="bold" />
                </button>

                <Field
                  className="h-9 w-24 rounded-lg border border-slate-300 text-center text-base font-medium text-gray-900 outline-0 transition focus:border-secondary"
                  id={`${setting.key}-value`}
                  name="value"
                  type="number"
                />

                <button
                  className={stepperButtonClasses}
                  disabled={isSubmitting || values.value >= setting.max}
                  type="button"
                  onClick={() =>
                    setFieldValue(
                      "value",
                      clamp(Number(values.value) + 1, setting)
                    )
                  }
                >
                  <Plus size={14} weight="bold" />
                </button>

                <span className="ml-1 text-xs text-gray-400">
                  {`${setting.min}–${setting.max} studies`}
                </span>

                <div className="ml-auto flex items-center gap-2">
                  {dirty && (
                    <button
                      className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition hover:bg-slate-100 hover:text-gray-700"
                      disabled={isSubmitting}
                      type="button"
                      onClick={() => resetForm()}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    className="rounded-lg bg-secondary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-secondary_dark disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!dirty || isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>

              <ErrorMessage name="value">
                {(message) => (
                  <p className="mt-2 text-xs text-rose-500">{message}</p>
                )}
              </ErrorMessage>
            </Form>
          )}
        </Formik>

        <p className="mt-5 text-xs text-gray-400">
          {describeLastChange(setting)}
        </p>
      </div>

      <div className="border-t border-slate-200 bg-slate-50">
        <button
          aria-expanded={isHistoryOpen}
          className="flex w-full items-center justify-between px-6 py-3 text-sm font-medium text-gray-600 transition hover:text-primary"
          type="button"
          onClick={() => setIsHistoryOpen((open) => !open)}
        >
          <span className="flex items-center gap-2">
            <ClockCounterClockwise size={16} />
            Change history
          </span>
          <CaretDown
            className={`transition-transform ${
              isHistoryOpen ? "rotate-180" : ""
            }`}
            size={14}
          />
        </button>

        {isHistoryOpen && (
          <div className="px-6 pb-5">
            <SettingHistory settingKey={setting.key} />
          </div>
        )}
      </div>
    </section>
  )
}

export default SettingCard
